import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FILING_TYPES, SERVICE_TYPES, CONTACT_METHODS, US_STATES } from '../data/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import * as Icons from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function StartHerePage() {
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', state: '', filingType: '', serviceNeeded: '',
    deadline: '', contactMethod: '', notes: '', consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.phone || !form.consent) {
      toast.error('Please fill in all required fields and accept the consent.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/intake`, form);
      setSubmitted(true);
      toast.success('Your information has been submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-[600px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center mx-auto mb-6">
              <Icons.CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-bold text-[rgb(0,55,32)] text-2xl mb-3">Thank You!</h1>
            <p className="text-[rgb(131,146,140)] leading-relaxed mb-2">We've received your information and will review it shortly.</p>
            <p className="text-[rgb(131,146,140)] leading-relaxed mb-6">Next steps: We'll review your submission and send you an invoice after our review. Expect to hear from us within 1 business day.</p>
            <a href="/book" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm no-underline hover:shadow-md transition-shadow">
              Book a Consultation <Icons.ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <section className="pt-32 pb-16 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.12) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.12) 0px, transparent 50%)' }}>
        <div className="max-w-[700px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">Intake Form</Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }} className="font-bold text-[rgb(0,55,32)] tracking-tight mb-3" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '0.92' }}>
            Start Here
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }} className="text-[rgb(131,146,140)]" style={{ fontSize: 'clamp(1rem, 2vw, 1.1rem)' }}>
            Fill out the form below and our team will review your information. We'll send an invoice after review.
          </motion.p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-[640px] mx-auto px-6">
          <motion.form {...fadeInUp} onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Full Name *</Label>
                <Input value={form.fullName} onChange={e => handleChange('fullName', e.target.value)} placeholder="John Doe" className="rounded-lg h-11" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Email *</Label>
                <Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} placeholder="john@example.com" className="rounded-lg h-11" required />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Phone *</Label>
                <Input type="tel" value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="(555) 123-4567" className="rounded-lg h-11" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">State(s)</Label>
                <Select onValueChange={v => handleChange('state', v)}>
                  <SelectTrigger className="rounded-lg h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Filing Type</Label>
                <Select onValueChange={v => handleChange('filingType', v)}>
                  <SelectTrigger className="rounded-lg h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {FILING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Service Needed</Label>
                <Select onValueChange={v => handleChange('serviceNeeded', v)}>
                  <SelectTrigger className="rounded-lg h-11"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Deadline Urgency</Label>
                <Select onValueChange={v => handleChange('deadline', v)}>
                  <SelectTrigger className="rounded-lg h-11"><SelectValue placeholder="Select urgency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (2-day turnaround)</SelectItem>
                    <SelectItem value="expedited">Expedited (+$99)</SelectItem>
                    <SelectItem value="extension">Filed extension</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Preferred Contact Method</Label>
                <Select onValueChange={v => handleChange('contactMethod', v)}>
                  <SelectTrigger className="rounded-lg h-11"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[rgb(0,55,32)]">Additional Notes</Label>
              <Textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="Anything else we should know about your tax situation..." className="rounded-lg min-h-[100px]" />
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="consent" checked={form.consent} onCheckedChange={v => handleChange('consent', v)} className="mt-0.5" />
              <Label htmlFor="consent" className="text-sm text-[rgb(131,146,140)] leading-relaxed cursor-pointer">
                I consent to Beginning to End Tax collecting and processing my information for tax preparation services. I understand that my information will be handled securely and confidentially. *
              </Label>
            </div>
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-base shadow-sm hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? 'Submitting...' : 'Submit Information'} {!loading && <Icons.ArrowRight className="w-4 h-4" />}
            </button>
          </motion.form>
        </div>
      </section>
    </main>
  );
}
